<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		DropdownMenu,
		MenuItemList,
		IconButton,
		ButtonV2,
		type IconButtonPropsTypes,
	} from '@Shelly/shelly-design-system';
	import TabsActionsButton from 'src/components/TabsActionsButton.svelte';
	import { cameraMonitoringActions, cameraMonitoringEditMode } from '../helpers/camera-monitoring-ui';
	import type { GridLayout } from '../helpers/camera-monitoring-settings';
	import type { MenuItemListExtendedProps } from 'src/components/Dashboards/dash-types';

	/**
	 * Shared "Manage dashboard" groups from the regular dashboard kebab
	 * (Dashboard Settings / Reorder / Delete / Add new). Passed in from
	 * MainLayoutNew so camera monitoring keeps parity with other dash tabs.
	 */
	export let manageDashboardMenuItems: MenuItemListExtendedProps[] = [];
	export let triggerVariant: IconButtonPropsTypes['variant'] = 'help-2';

	let showDropdown = false;

	$: actions = $cameraMonitoringActions;

	const enterReorder = () => {
		cameraMonitoringEditMode.set(true);
		showDropdown = false;
	};

	const exitReorder = () => cameraMonitoringEditMode.set(false);

	const onEditWidgets = () => {
		actions?.onOpenSettings();
		showDropdown = false;
	};

	// Layout picks keep the menu open so the selection is visible and easy to retry.
	const onLayoutClick = (layout: GridLayout) => actions?.onLayoutChange(layout);

	const onManageItemClick = (item: MenuItemListExtendedProps) => {
		showDropdown = false;
		item.onClick?.(item);
	};
</script>

{#if $cameraMonitoringEditMode}
	<ButtonV2 size="m" variant="secondary" icon="fas fa-xmark" on:click={exitReorder}>
		{$format('button_exit_edit', { default: 'Exit edit mode' })}
	</ButtonV2>
{:else if actions}
	<DropdownMenu bind:show={showDropdown}>
		<TabsActionsButton
			slot="trigger"
			variant={triggerVariant}
			isActive={showDropdown}
			ariaLabel={$format('aria_camera_actions', { default: 'Camera dashboard actions' })}
			on:click={() => (showDropdown = !showDropdown)}
		/>

		{#if actions.availableGridLayouts.length > 1}
			<MenuItemList
				isGroupTitle
				size="m"
				title={$format('dropdown_group_layout', { default: 'Layout' })}
			/>
			<div class="camera-dash-actions-layouts">
				{#each actions.availableGridLayouts as layout (layout.value)}
					<IconButton
						size="m"
						icon={layout.icon}
						variant={actions.selectedLayout === layout.value ? 'primary' : 'secondary'}
						aria-label={`${layout.value} grid layout`}
						on:click={() => onLayoutClick(layout.value)}
					/>
				{/each}
			</div>
		{/if}

		<MenuItemList
			isGroupTitle
			size="m"
			title={$format('dropdown_group_dashboard', { default: 'Dashboard' })}
		/>
		<MenuItemList
			isGroupItem
			size="m"
			title={$format('label_edit_widgets', { default: 'Edit widgets' })}
			icon="fas fa-pen-to-square"
			on:click={onEditWidgets}
		/>
		<MenuItemList
			isGroupItem
			size="m"
			title={$format('dropdown_reorder_cameras', { default: 'Reorder cameras' })}
			icon="fas fa-arrows-up-down-left-right"
			disabled={!actions.hasCameras || actions.selectedLayout === 'focus'}
			on:click={enterReorder}
		/>
		<!-- Hide offline cameras: commented out while placeholders cover empty slots.
		<MenuItemList
			isGroupItem
			size="m"
			title={$format('label_hide_offline_cameras', { default: 'Hide offline cameras' })}
			icon="fas fa-video-slash"
			on:click={() => actions?.onToggleHideOffline()}
		>
			<div slot="actions" class="camera-dash-actions-toggle">
				<Toggle id="camera-hide-offline-toggle" size="s" checked={actions.hideOfflineCameras} />
			</div>
		</MenuItemList>
		-->

		{#each manageDashboardMenuItems as groupItem, groupIndex}
			{#if groupItem.title}
				<MenuItemList isGroupTitle={groupItem.isGroupTitle} size="m" title={groupItem.title} />
			{:else if groupIndex > 0}
				<div class="camera-dash-actions-divider" />
			{/if}
			{#each groupItem.items || [] as menuListItem}
				<MenuItemList
					isGroupItem
					size="m"
					title={menuListItem.title}
					titleClasses={menuListItem.titleClasses}
					icon={menuListItem.icon}
					iconStyle={menuListItem?.iconStyle}
					disabled={menuListItem.disabled}
					on:click={() => onManageItemClick(menuListItem)}
				/>
			{/each}
		{/each}
	</DropdownMenu>
{/if}

<style>
	.camera-dash-actions-layouts {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);
		padding: var(--spacing-xs) var(--spacing-s) var(--spacing-s);
	}

	.camera-dash-actions-divider {
		border-top: 1px solid var(--color-3);
	}
</style>
